import { RequestId } from '@modelcontextprotocol/sdk/types.js';

import { getConfig } from './config.js';
import { log } from './logging/logger.js';
import { notifier, shouldNotifyWhenLevelIsAtLeast } from './logging/notification.js';
import { maskRequest, maskResponse } from './logging/secretMask.js';
import {
  AxiosResponseInterceptorConfig,
  ErrorInterceptor,
  getRequestInterceptorConfig,
  getResponseInterceptorConfig,
  RequestInterceptor,
  RequestInterceptorConfig,
  ResponseInterceptor,
  ResponseInterceptorConfig,
} from './sdks/interceptors.js';
import { buildAuthConfig } from './sdks/tableau/buildAuthConfig.js';
import { RestApi } from './sdks/tableau/restApi.js';
import { Server } from './server.js';
import { TableauWebRequestHandlerExtra } from './tools/web/toolContext.js';
import { isAxiosError } from './utils/axios.js';
import { getExceptionMessage } from './utils/getExceptionMessage.js';
import invariant from './utils/invariant.js';

type JwtScopes =
  | 'tableau:viz_data_service:read'
  | 'tableau:content:read'
  | 'tableau:insight_definitions_metrics:read'
  | 'tableau:insight_metrics:read'
  | 'tableau:metric_subscriptions:read'
  | 'tableau:insights:read'
  | 'tableau:views:download'
  | 'tableau:views:embed'
  | 'tableau:insight_brief:create'
  | 'tableau:mcp_site_settings:read'
  | 'tableau:tasks:read'
  | 'tableau:tasks:delete'
  | 'tableau:tasks:write'
  | 'tableau:workbook_tags:update'
  | 'tableau:workbooks:download'
  | 'tableau:workbooks:delete'
  | 'tableau:workbooks:create'
  | 'tableau:file_uploads:create'
  | 'tableau:datasource_tags:update'
  | 'tableau:datasources:delete'
  | 'tableau:jobs:read'
  | 'tableau:flow_tasks:read'
  | 'tableau:users:read'
  | 'tableau:users:update'
  | 'tableau:flows:read'
  | 'tableau:flow_connections:read'
  | 'tableau:flow_runs:read';

export type RestApiArgs = Pick<
  TableauWebRequestHandlerExtra,
  'config' | 'server' | 'signal' | 'tableauAuthInfo' | 'setSiteLuid' | 'setUserLuid'
> &
  Partial<Pick<TableauWebRequestHandlerExtra, 'getSiteLuid' | 'getUserLuid'>> &
  (
    | {
        requestId: RequestId;
        disableLogging?: false;
      }
    | {
        disableLogging: true;
      }
  );

const getNewRestApiInstanceAsync = async (
  args: RestApiArgs & {
    jwtScopes: Set<JwtScopes>;
  },
): Promise<{ restApi: RestApi; signOutWhenCompleted: boolean }> => {
  const {
    config,
    server,
    jwtScopes,
    signal,
    tableauAuthInfo,
    disableLogging,
    setSiteLuid,
    setUserLuid,
    getSiteLuid,
    getUserLuid,
  } = args;

  if (!disableLogging) {
    const { requestId } = args;
    signal.addEventListener(
      'abort',
      () => {
        notifier.info(
          server.mcpServer,
          {
            type: 'request-cancelled',
            requestId,
            reason: signal.reason,
          },
          { notifier: server.name, requestId },
        );
      },
      { once: true },
    );
  }

  const tableauServer = config.server || tableauAuthInfo?.server;
  invariant(tableauServer, 'Tableau server could not be determined');

  const restApi = new RestApi({
    maxRequestTimeoutMs: config.maxRequestTimeoutMs,
    signal,
    requestInterceptor: disableLogging
      ? undefined
      : [
          getRequestInterceptor(server, args.requestId),
          getRequestErrorInterceptor(server, args.requestId, { getSiteLuid, getUserLuid }),
        ],
    responseInterceptor: disableLogging
      ? undefined
      : [
          getResponseInterceptor(server, args.requestId),
          getResponseErrorInterceptor(server, args.requestId, { getSiteLuid, getUserLuid }),
        ],
  });

  let signOutWhenCompleted = true;
  if (tableauAuthInfo?.type === 'Passthrough') {
    if (!tableauAuthInfo.raw || !tableauAuthInfo.userId) {
      throw new Error('Auth info is required when not signing in first.');
    }

    signOutWhenCompleted = false;
    restApi.setCredentials(tableauAuthInfo.raw, tableauAuthInfo.userId);
  } else {
    const authConfig = buildAuthConfig({ config, tableauAuthInfo, scopes: jwtScopes });
    if (authConfig) {
      await restApi.signIn(authConfig);
      setSiteLuid?.(restApi.siteId);
      setUserLuid?.(restApi.userId);
    } else {
      // oauth: buildAuthConfig returns null — preserve the existing oauth handling.
      invariant(tableauAuthInfo, 'Tableau auth info not provided.');

      signOutWhenCompleted = false;
      if (tableauAuthInfo?.type === 'Bearer') {
        restApi.setBearerToken(tableauAuthInfo.raw);
      } else if (tableauAuthInfo?.type === 'X-Tableau-Auth') {
        if (!tableauAuthInfo?.accessToken || !tableauAuthInfo?.userId) {
          throw new Error('Auth info is required when not signing in first.');
        }

        restApi.setCredentials(tableauAuthInfo.accessToken, tableauAuthInfo.userId);
      } else {
        throw new Error('Auth info is required when not signing in first.');
      }
    }
  }

  return { restApi, signOutWhenCompleted };
};

export const useRestApi = async <T>(
  args: RestApiArgs & {
    jwtScopes: ReadonlyArray<JwtScopes>;
    callback: (restApi: RestApi) => Promise<T>;
  },
): Promise<T> => {
  const { callback, ...remaining } = args;
  const { restApi, signOutWhenCompleted } = await getNewRestApiInstanceAsync({
    ...remaining,
    jwtScopes: new Set(args.jwtScopes),
  });
  const logCtx = { getSiteLuid: args.getSiteLuid, getUserLuid: args.getUserLuid };
  try {
    return await callback(restApi);
  } finally {
    if (signOutWhenCompleted) {
      // Tableau REST sessions for 'pat' and 'direct-trust' are intentionally ephemeral.
      // Sessions for 'oauth' and 'passthrough' are not. Signing out would invalidate the session,
      // preventing the access token from being reused for subsequent requests.
      //
      // Isolate the sign-out so a teardown failure can NEVER mask the callback's real result or
      // error. A throw inside `finally` replaces whatever the `try` was returning or throwing, so an
      // un-caught sign-out error would clobber the real outcome — e.g. a callback that 404s on a
      // missing resource surfaces to the caller as the sign-out's 401 once the session is torn down
      // (W-23202034). Swallow-and-log instead: sign-out is best-effort cleanup, and the ephemeral
      // session expires on its own regardless.
      try {
        await restApi.signOut();
        log(
          {
            message: 'Signed out of Tableau REST API',
            level: 'debug',
            logger: 'auth',
          },
          logCtx,
        );
      } catch (error) {
        log(
          {
            message: `Failed to sign out of Tableau REST API: ${getExceptionMessage(error)}`,
            level: 'warning',
            logger: 'auth',
            data: error,
          },
          logCtx,
        );
      }
    }
  }
};

export const getRequestInterceptor =
  (server: Server, requestId: RequestId): RequestInterceptor =>
  (request) => {
    request.headers['User-Agent'] = server.userAgent;
    logRequest(server, request, requestId);
    return request;
  };

export const getRequestErrorInterceptor =
  (
    server: Server,
    requestId: RequestId,
    ctx?: { getSiteLuid?: () => string; getUserLuid?: () => string },
  ): ErrorInterceptor =>
  (error, baseUrl) => {
    if (!isAxiosError(error) || !error.request) {
      log(
        {
          message: `Request ${requestId} failed`,
          level: 'error',
          logger: 'rest-api',
          data: error,
        },
        ctx,
      );
      notifier.error(
        server.mcpServer,
        `Request ${requestId} failed with error: ${getExceptionMessage(error)}`,
        {
          notifier: 'rest-api',
          requestId,
        },
      );
      return;
    }

    const { request } = error;
    logRequest(
      server,
      {
        baseUrl,
        ...getRequestInterceptorConfig(request),
      },
      requestId,
    );
  };

export const getResponseInterceptor =
  (server: Server, requestId: RequestId): ResponseInterceptor =>
  (response) => {
    logResponse(server, response, requestId);
    return response;
  };

export const getResponseErrorInterceptor =
  (
    server: Server,
    requestId: RequestId,
    ctx?: { getSiteLuid?: () => string; getUserLuid?: () => string },
  ): ErrorInterceptor =>
  (error, baseUrl) => {
    if (!isAxiosError(error) || !error.response) {
      log(
        {
          message: `Response from request ${requestId} failed`,
          level: 'error',
          logger: 'rest-api',
          data: error,
        },
        ctx,
      );
      notifier.error(
        server.mcpServer,
        `Response from request ${requestId} failed with error: ${getExceptionMessage(error)}`,
        { notifier: 'rest-api', requestId },
      );
      return;
    }

    // The type for the AxiosResponse headers is complex and not directly assignable to that of the Axios response interceptor's.
    const { response } = error as { response: AxiosResponseInterceptorConfig };
    logResponse(
      server,
      {
        baseUrl,
        ...getResponseInterceptorConfig(response),
      },
      requestId,
    );
  };

function logRequest(server: Server, request: RequestInterceptorConfig, requestId: RequestId): void {
  const config = getConfig();
  const maskedRequest = config.disableLogMasking ? request : maskRequest(request);
  const url = new URL(
    `${maskedRequest.baseUrl.replace(/\/$/, '')}/${maskedRequest.url?.replace(/^\//, '') ?? ''}`,
  );
  if (request.params && Object.keys(request.params).length > 0) {
    url.search = new URLSearchParams(request.params).toString();
  }

  const messageObj = {
    type: 'request',
    requestId,
    method: maskedRequest.method,
    url: url.toString(),
    ...(shouldNotifyWhenLevelIsAtLeast('debug') && {
      headers: maskedRequest.headers,
      data: maskedRequest.data,
      params: maskedRequest.params,
    }),
  } as const;

  notifier.info(server.mcpServer, messageObj, { notifier: 'rest-api', requestId });
}

function logResponse(
  server: Server,
  response: ResponseInterceptorConfig,
  requestId: RequestId,
): void {
  const config = getConfig();
  const maskedResponse = config.disableLogMasking ? response : maskResponse(response);
  const url = new URL(
    `${maskedResponse.baseUrl.replace(/\/$/, '')}/${maskedResponse.url?.replace(/^\//, '') ?? ''}`,
  );
  if (response.params && Object.keys(response.params).length > 0) {
    url.search = new URLSearchParams(response.params).toString();
  }
  const messageObj = {
    type: 'response',
    requestId,
    url: url.toString(),
    status: maskedResponse.status,
    ...(shouldNotifyWhenLevelIsAtLeast('debug') && {
      headers: maskedResponse.headers,
      data: maskedResponse.data,
    }),
  } as const;

  notifier.info(server.mcpServer, messageObj, { notifier: 'rest-api', requestId });
}
